/**
 * Callout component — shared types and Zod prop schema.
 *
 * A persistent informational banner (distinct from the transient Toast): a
 * coloured surface holding an optional icon, an optional title, and a body.
 * The `tone` axis selects the surface colour/emphasis of one coherent layout
 * (ADR-020 Rule 6 — one component, not many). Only `success` ships today.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { typography } from '../../elements/typography';
import { IconNameEnum } from '../Icon/Icon';

// Shape/role-based values (ADR-020 Rule 4) — never use-case names. `info` /
// `warning` / `critical` are future additive variants, not new components.
export const CalloutTone = z.enum(['success']);
export type CalloutTone = z.infer<typeof CalloutTone>;

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const calloutPropsSchema = z.object({
  tone: CalloutTone.default('success'),
  title: z.string().optional(),
  body: z.string(),
  icon: IconNameEnum.optional(),
});

export type CalloutProps = z.infer<typeof calloutPropsSchema>;

export const CALLOUT_RADIUS = radius.xl;
export const CALLOUT_PADDING = spacing.m;
/** Gap between the leading icon and the text column. */
export const CALLOUT_GAP = spacing.s;
/** Gap between the title and the body. */
export const CALLOUT_TEXT_GAP = spacing.s;
export const CALLOUT_ICON_SIZE = sizing.icon.sm;

export const CALLOUT_TITLE_TYPOGRAPHY = typography.b1Bold;
export const CALLOUT_BODY_TYPOGRAPHY = typography.b2Regular;
export const CALLOUT_TEXT_COLOR = colors.text.primary;

/** Surface colour per tone. */
export const calloutBackgroundForTone: Record<CalloutTone, string> = {
  success: colors.mossGreen['500'],
};

/** Leading-icon colour per tone. */
export const calloutIconColorForTone: Record<CalloutTone, string> = {
  success: colors.text.primary,
};
